import { useCallback, useEffect, useMemo, useState } from 'react';
import { adminByCountry, adminByPlatform, adminSummary, adminTimeseries } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  BarChart, Bar, Legend,
} from 'recharts';
import {
  Facebook, Instagram, Youtube, Globe2, Search, Send, Twitter,
  Linkedin, MousePointer2, RefreshCcw, CalendarRange,
} from 'lucide-react';

const FLAGS = {
  BD: '\u{1F1E7}\u{1F1E9}', IN: '\u{1F1EE}\u{1F1F3}', PK: '\u{1F1F5}\u{1F1F0}', NP: '\u{1F1F3}\u{1F1F5}', LK: '\u{1F1F1}\u{1F1F0}',
  US: '\u{1F1FA}\u{1F1F8}', CA: '\u{1F1E8}\u{1F1E6}', MX: '\u{1F1F2}\u{1F1FD}',
  GB: '\u{1F1EC}\u{1F1E7}', DE: '\u{1F1E9}\u{1F1EA}', FR: '\u{1F1EB}\u{1F1F7}', IT: '\u{1F1EE}\u{1F1F9}', ES: '\u{1F1EA}\u{1F1F8}', NL: '\u{1F1F3}\u{1F1F1}',
  CH: '\u{1F1E8}\u{1F1ED}', SE: '\u{1F1F8}\u{1F1EA}', NO: '\u{1F1F3}\u{1F1F4}', DK: '\u{1F1E9}\u{1F1F0}', PL: '\u{1F1F5}\u{1F1F1}',
  AE: '\u{1F1E6}\u{1F1EA}', SA: '\u{1F1F8}\u{1F1E6}', QA: '\u{1F1F6}\u{1F1E6}', KW: '\u{1F1F0}\u{1F1FC}', OM: '\u{1F1F4}\u{1F1F2}', BH: '\u{1F1E7}\u{1F1ED}',
  TR: '\u{1F1F9}\u{1F1F7}', EG: '\u{1F1EA}\u{1F1EC}', IL: '\u{1F1EE}\u{1F1F1}',
  JP: '\u{1F1EF}\u{1F1F5}', KR: '\u{1F1F0}\u{1F1F7}', CN: '\u{1F1E8}\u{1F1F3}', HK: '\u{1F1ED}\u{1F1F0}', TW: '\u{1F1F9}\u{1F1FC}',
  SG: '\u{1F1F8}\u{1F1EC}', MY: '\u{1F1F2}\u{1F1FE}', ID: '\u{1F1EE}\u{1F1E9}', TH: '\u{1F1F9}\u{1F1ED}', PH: '\u{1F1F5}\u{1F1ED}', VN: '\u{1F1FB}\u{1F1F3}',
  AU: '\u{1F1E6}\u{1F1FA}', NZ: '\u{1F1F3}\u{1F1FF}',
  BR: '\u{1F1E7}\u{1F1F7}', AR: '\u{1F1E6}\u{1F1F7}',
  ZA: '\u{1F1FF}\u{1F1E6}', NG: '\u{1F1F3}\u{1F1EC}', KE: '\u{1F1F0}\u{1F1EA}',
  RU: '\u{1F1F7}\u{1F1FA}',
};

const PLATFORM_META = {
  facebook: { label: 'Facebook', color: '#1877F2', Icon: Facebook },
  instagram: { label: 'Instagram', color: '#E1306C', Icon: Instagram },
  tiktok: { label: 'TikTok', color: '#FF0050', Icon: MousePointer2 },
  google: { label: 'Google Ads', color: '#F4B400', Icon: Search },
  youtube: { label: 'YouTube', color: '#FF0000', Icon: Youtube },
  telegram: { label: 'Telegram', color: '#27A7E7', Icon: Send },
  twitter: { label: 'Twitter / X', color: '#8A8A8A', Icon: Twitter },
  linkedin: { label: 'LinkedIn', color: '#0A66C2', Icon: Linkedin },
  snapchat: { label: 'Snapchat', color: '#FFFC00', Icon: MousePointer2 },
  direct: { label: 'Direct', color: '#9CA3AF', Icon: Globe2 },
  other: { label: 'Other', color: '#6B7280', Icon: Globe2 },
};

const QUICK_RANGES = [
  { key: '24h', label: 'Last 24h' },
  { key: '7d', label: '7 days' },
  { key: '15d', label: '15 days' },
  { key: '30d', label: '30 days' },
  { key: 'all', label: 'All time' },
];

function todayIso() {
  return new Date().toISOString().slice(0, 10);
}
function daysAgoIso(n) {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
}

export default function AdminAnalyticsTab() {
  const [summary, setSummary] = useState(null);
  const [byCountry, setByCountry] = useState([]);
  const [byPlatform, setByPlatform] = useState([]);
  const [series, setSeries] = useState([]);
  const [loading, setLoading] = useState(false);

  // Filter state: quick preset OR custom range
  const [quick, setQuick] = useState('7d');
  const [customStart, setCustomStart] = useState(daysAgoIso(7));
  const [customEnd, setCustomEnd] = useState(todayIso());
  const [useCustom, setUseCustom] = useState(false);

  const buildParams = useCallback(() => {
    if (useCustom) {
      return { start: customStart, end: customEnd };
    }
    return { quick };
  }, [useCustom, quick, customStart, customEnd]);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    const params = buildParams();
    try {
      const [s, c, p, t] = await Promise.all([
        adminSummary(params),
        adminByCountry(params),
        adminByPlatform(params),
        adminTimeseries(params),
      ]);
      setSummary(s);
      setByCountry(c.rows || []);
      setByPlatform(p.rows || []);
      setSeries(t.series || []);
    } catch (e) { /* noop */ }
    finally { setLoading(false); }
  }, [buildParams]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const kpis = [
    { label: 'Total Visits', value: summary?.total_visits ?? 0, test: 'kpi-visits' },
    { label: 'Unique Visitors', value: summary?.unique_visitors ?? 0, test: 'kpi-unique' },
    { label: 'Telegram Clicks', value: summary?.total_tg_clicks ?? 0, test: 'kpi-tg' },
    { label: 'Conversion Rate', value: `${summary?.conversion_rate ?? 0}%`, test: 'kpi-cvr' },
  ];

  const platformChartData = useMemo(() => {
    // Only show platforms with any traffic in selected range
    return byPlatform
      .filter(r => (r.visits + r.tg_clicks) > 0)
      .map(r => ({
        ...r,
        label: PLATFORM_META[r.platform]?.label || r.platform,
      }));
  }, [byPlatform]);

  const rangeDescription = useMemo(() => {
    if (useCustom) return `${customStart} → ${customEnd}`;
    const q = QUICK_RANGES.find(r => r.key === quick);
    return q ? q.label : quick;
  }, [useCustom, customStart, customEnd, quick]);

  return (
    <div className="space-y-5">
      {/* ------- Time range filter bar ------- */}
      <Card className="card-surface" data-testid="admin-analytics-filter">
        <CardContent className="p-4 space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <CalendarRange className="h-4 w-4 text-[hsl(43_92%_56%)]" />
            <span className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground mr-2">Time range</span>
            {QUICK_RANGES.map(r => {
              const active = !useCustom && quick === r.key;
              return (
                <Button
                  key={r.key}
                  type="button"
                  size="sm"
                  variant={active ? 'default' : 'outline'}
                  className={active ? 'h-8 cta-sheen text-white border-0' : 'h-8 border-white/10 bg-white/[0.02] text-muted-foreground hover:text-white'}
                  onClick={() => { setUseCustom(false); setQuick(r.key); }}
                  data-testid={`range-quick-${r.key}`}
                >
                  {r.label}
                </Button>
              );
            })}
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="h-8 ml-auto border-white/10 bg-white/[0.02] text-muted-foreground hover:text-white"
              onClick={fetchAll}
              disabled={loading}
              data-testid="range-refresh"
            >
              <RefreshCcw className={`h-3.5 w-3.5 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
            </Button>
          </div>
          <div className="flex flex-wrap items-end gap-3 pt-1">
            <div className="flex items-center gap-2">
              <input
                id="use-custom"
                type="checkbox"
                checked={useCustom}
                onChange={(e) => setUseCustom(e.target.checked)}
                className="h-4 w-4 accent-[hsl(43_92%_56%)]"
                data-testid="range-custom-toggle"
              />
              <Label htmlFor="use-custom" className="text-xs text-muted-foreground cursor-pointer">
                Custom range
              </Label>
            </div>
            <div className="flex items-end gap-2">
              <div>
                <Label htmlFor="start" className="text-[10px] uppercase tracking-wider text-muted-foreground">From</Label>
                <Input
                  id="start"
                  type="date"
                  value={customStart}
                  onChange={(e) => setCustomStart(e.target.value)}
                  disabled={!useCustom}
                  max={customEnd}
                  className="h-9 w-[160px] bg-white/[0.02] border-white/10"
                  data-testid="range-custom-start"
                />
              </div>
              <div>
                <Label htmlFor="end" className="text-[10px] uppercase tracking-wider text-muted-foreground">To</Label>
                <Input
                  id="end"
                  type="date"
                  value={customEnd}
                  onChange={(e) => setCustomEnd(e.target.value)}
                  disabled={!useCustom}
                  min={customStart}
                  max={todayIso()}
                  className="h-9 w-[160px] bg-white/[0.02] border-white/10"
                  data-testid="range-custom-end"
                />
              </div>
            </div>
            <div className="text-xs text-muted-foreground ml-auto" data-testid="range-description">
              Showing: <span className="text-white font-medium">{rangeDescription}</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* ------- KPIs ------- */}
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {kpis.map(k => (
          <Card key={k.label} className="card-surface" data-testid={`admin-${k.test}`}>
            <CardContent className="p-5">
              <div className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground">{k.label}</div>
              <div className="font-display uppercase text-3xl mt-2 text-[hsl(43_92%_56%)]">{k.value}</div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* ------- Facebook Ads attribution ------- */}
      <Card className="card-surface border-[rgba(108,176,255,0.32)]" data-testid="admin-fb-ads-card">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <CardTitle className="text-base flex items-center gap-2">
              <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-[10px] font-bold border uppercase tracking-[0.14em] bg-[rgba(108,176,255,0.14)] text-[hsl(212_80%_70%)] border-[rgba(108,176,255,0.42)]">
                FB ADS
              </span>
              Facebook Ads Attribution · <span className="text-muted-foreground font-normal">{rangeDescription}</span>
            </CardTitle>
            <span className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
              Compare with FB Ads Manager
            </span>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid gap-3 grid-cols-2 lg:grid-cols-4">
            <div className="rounded-lg border border-white/10 bg-white/[0.02] p-4" data-testid="kpi-fb-visits">
              <div className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground">FB Visits (all pages)</div>
              <div className="font-display uppercase text-2xl mt-1 text-[hsl(212_80%_70%)]">
                {summary?.fb_ads?.visits ?? 0}
              </div>
            </div>
            <div className="rounded-lg border border-white/10 bg-white/[0.02] p-4" data-testid="kpi-fb-tg">
              <div className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground">FB Telegram Clicks</div>
              <div className="font-display uppercase text-2xl mt-1 text-[hsl(212_80%_70%)]">
                {summary?.fb_ads?.tg_clicks ?? 0}
              </div>
            </div>
            <div className="rounded-lg border border-white/10 bg-white/[0.02] p-4" data-testid="kpi-fb-join-visits">
              <div className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground">/join Page Visits</div>
              <div className="font-display uppercase text-2xl mt-1 text-[hsl(212_80%_70%)]">
                {summary?.fb_ads?.join_page_visits ?? 0}
              </div>
            </div>
            <div className="rounded-lg border-2 border-[rgba(108,176,255,0.42)] bg-[rgba(108,176,255,0.06)] p-4" data-testid="kpi-fb-join-tg">
              <div className="text-[11px] uppercase tracking-[0.2em] text-[hsl(212_80%_70%)] font-semibold">/join → Telegram Clicks ⭐</div>
              <div className="font-display uppercase text-3xl mt-1 text-white">
                {summary?.fb_ads?.join_page_tg_clicks ?? 0}
              </div>
              <div className="mt-1 text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                Compare with FB ads "Subscribe"
              </div>
            </div>
          </div>
          <div className="text-xs text-muted-foreground leading-relaxed pt-1">
            <strong className="text-[hsl(212_80%_70%)]">FB conversion rate:</strong>{' '}
            <span className="text-white font-semibold">{summary?.fb_ads?.conversion_rate ?? 0}%</span>
            {' · '}
            Attribution = events e <code className="text-[hsl(43_92%_56%)]">fbclid</code> URL parameter ase OR <code className="text-[hsl(43_92%_56%)]">platform = facebook</code> detect hoyeche. <span className="text-white">FB Ads Manager er "Subscribe" event count</span> ar amader <span className="text-white">"/join → Telegram Clicks"</span> number proximate match korbe (FB pixel + CAPI dedupe er karone 5-15% variance normal).
          </div>
        </CardContent>
      </Card>

      {/* ------- Timeseries chart ------- */}
      <Card className="card-surface" data-testid="chart-timeseries">
        <CardHeader><CardTitle className="text-base">Visits vs Telegram clicks · {rangeDescription}</CardTitle></CardHeader>
        <CardContent className="h-72">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={series}>
              <CartesianGrid stroke="rgba(255,255,255,0.05)" strokeDasharray="3 3" />
              <XAxis dataKey="day" stroke="#888" fontSize={11} />
              <YAxis stroke="#888" fontSize={11} allowDecimals={false} />
              <Tooltip contentStyle={{ background: '#17171B', border: '1px solid #2B2B33' }} />
              <Legend />
              <Line type="monotone" dataKey="visits" stroke="#F5B942" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="tg_clicks" stroke="#E11D2E" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* ------- Traffic by Platform ------- */}
      <Card className="card-surface" data-testid="chart-platform">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            Traffic by platform
            <span className="text-[11px] text-muted-foreground font-normal">· detected from UTM source & referrer</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {platformChartData.length === 0 ? (
            <div className="text-sm text-muted-foreground text-center py-8" data-testid="platform-empty">
              No platform traffic in this range yet.
            </div>
          ) : (
            <>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={platformChartData}>
                    <CartesianGrid stroke="rgba(255,255,255,0.05)" strokeDasharray="3 3" />
                    <XAxis dataKey="label" stroke="#888" fontSize={11} />
                    <YAxis stroke="#888" fontSize={11} allowDecimals={false} />
                    <Tooltip contentStyle={{ background: '#17171B', border: '1px solid #2B2B33' }} />
                    <Legend />
                    <Bar dataKey="visits" name="Visits" fill="#F5B942" radius={[6, 6, 0, 0]} />
                    <Bar dataKey="tg_clicks" name="Telegram clicks" fill="#E11D2E" radius={[6, 6, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>

              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Platform</TableHead>
                    <TableHead className="text-right">Visits</TableHead>
                    <TableHead className="text-right">TG Clicks</TableHead>
                    <TableHead className="text-right">CVR</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {platformChartData.map(r => {
                    const meta = PLATFORM_META[r.platform] || PLATFORM_META.other;
                    const Icon = meta.Icon;
                    return (
                      <TableRow key={r.platform} data-testid={`platform-row-${r.platform}`}>
                        <TableCell className="font-medium">
                          <span className="inline-flex items-center gap-2">
                            <Icon className="h-4 w-4" style={{ color: meta.color }} />
                            {meta.label}
                          </span>
                        </TableCell>
                        <TableCell className="text-right">{r.visits}</TableCell>
                        <TableCell className="text-right">{r.tg_clicks}</TableCell>
                        <TableCell className="text-right text-[hsl(43_92%_56%)]">{r.conversion_rate}%</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </>
          )}
        </CardContent>
      </Card>

      {/* ------- Country bar chart ------- */}
      <Card className="card-surface" data-testid="chart-country">
        <CardHeader><CardTitle className="text-base">Telegram clicks by country</CardTitle></CardHeader>
        <CardContent className="h-72">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={byCountry.slice(0, 10)}>
              <CartesianGrid stroke="rgba(255,255,255,0.05)" strokeDasharray="3 3" />
              <XAxis dataKey="country_code" stroke="#888" fontSize={11} />
              <YAxis stroke="#888" fontSize={11} allowDecimals={false} />
              <Tooltip contentStyle={{ background: '#17171B', border: '1px solid #2B2B33' }} />
              <Bar dataKey="tg_clicks" fill="#E11D2E" radius={[6,6,0,0]} />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* ------- Country breakdown table ------- */}
      <Card className="card-surface">
        <CardHeader><CardTitle className="text-base">Country breakdown</CardTitle></CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Country</TableHead>
                <TableHead className="text-right">Visits</TableHead>
                <TableHead className="text-right">TG Clicks</TableHead>
                <TableHead className="text-right">CVR</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {byCountry.map(r => (
                <TableRow key={r.country_code}>
                  <TableCell className="font-medium">{FLAGS[r.country_code] || '🌐'} {r.country_name || r.country_code}</TableCell>
                  <TableCell className="text-right">{r.visits}</TableCell>
                  <TableCell className="text-right">{r.tg_clicks}</TableCell>
                  <TableCell className="text-right text-[hsl(43_92%_56%)]">{r.conversion_rate}%</TableCell>
                </TableRow>
              ))}
              {byCountry.length === 0 && (
                <TableRow><TableCell colSpan={4} className="text-muted-foreground text-sm text-center py-6">No data yet. Open the site to record events.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
